/**
 * Authentication utilities for the frontend
 *
 * Better Auth uses HttpOnly session cookies for security.
 * For API calls to FastAPI backend, include credentials: 'include'
 * to automatically send cookies. The backend validates via session introspection.
 */

import {
  signIn as authSignIn,
  signOut as authSignOut,
  getSession as authGetSession,
  getCurrentUser,
  Session,
  User,
} from "./auth-client";

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string) {
  const result = await authSignIn(email, password);

  if (result.error) {
    // Handle error object or string
    const errorMessage =
      typeof result.error === "string"
        ? result.error
        : (result.error as any)?.message || "Authentication failed";
    throw new Error(errorMessage);
  }

  return result.data;
}

/**
 * Sign out the current user
 */
export async function signOut() {
  _updateCachedToken(""); // Clear persisted token
  await authSignOut();
}

/**
 * Get the current session
 */
export async function getSession(): Promise<Session | null> {
  return authGetSession();
}

/**
 * Get the current user
 */
export async function getUser(): Promise<User | null> {
  return getCurrentUser();
}

/**
 * Get fetch options for authenticated API requests
 * Better Auth uses cookies, so we just need credentials: 'include'
 */
export function getAuthFetchOptions(): RequestInit {
  return {
    credentials: "include",
  };
}

/**
 * Make an authenticated fetch request to the API
 * Automatically includes credentials for cookie-based auth
 */
export async function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
}

/**
 * Refresh the session
 */
export async function refreshSession() {
  return getSession();
}

/**
 * Get the Mstro Hub URL for registration
 */
export function getMstroHubSignupUrl(): string {
  return process.env.NEXT_PUBLIC_MSTROHUB_URL
    ? `${process.env.NEXT_PUBLIC_MSTROHUB_URL}/signup`
    : "https://io.mstrohub.com/signup";
}

/**
 * Get the Mstro Hub URL for password reset
 */
export function getMstroHubResetPasswordUrl(): string {
  return process.env.NEXT_PUBLIC_MSTROHUB_URL
    ? `${process.env.NEXT_PUBLIC_MSTROHUB_URL}/reset-password`
    : "https://io.mstrohub.com/reset-password";
}

/**
 * Session token storage key
 */
const TOKEN_STORAGE_KEY = "hive_session_token";

/**
 * Get cached token from sessionStorage (persists across page refresh)
 */
function getCachedToken(): string {
  if (typeof window === "undefined") return "";
  return sessionStorage.getItem(TOKEN_STORAGE_KEY) || "";
}

/**
 * Update the cached token (called after login)
 * Persists to sessionStorage to survive page refresh
 */
export function _updateCachedToken(token: string) {
  if (typeof window === "undefined") return;
  if (token) {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

/**
 * Clear all Better Auth session cookies from the browser
 * This is used when we detect stale/invalid sessions to prevent login loops
 */
export function clearAuthCookies() {
  if (typeof document === "undefined") return;

  // Better Auth cookie names
  const cookieNames = [
    "better-auth.session_token",
    "better-auth.session_data",
    "__Secure-better-auth.session_token",
    "__Secure-better-auth.session_data",
  ];

  // Clear each cookie by setting it to expire in the past
  // We try multiple path and domain combinations to ensure complete cleanup
  cookieNames.forEach((name) => {
    // Clear for current path
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    // Clear for root path
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    // Clear without domain (for localhost)
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
  });

  // Also clear sessionStorage token
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * Get authorization headers for API requests
 *
 * In development: Returns Bearer token header (cross-port cookie workaround)
 * In production: Returns empty (Better Auth uses cookies automatically)
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  // Try cached token first (from sessionStorage)
  const cachedToken = getCachedToken();
  if (cachedToken) {
    return { Authorization: `Bearer ${cachedToken}` };
  }

  // Try to get token from session
  try {
    const session = await authGetSession();
    if (session?.token) {
      _updateCachedToken(session.token);
      return { Authorization: `Bearer ${session.token}` };
    }
  } catch (error) {
    console.error("Failed to get session for auth headers:", error);
  }

  return {};
}

/**
 * Get auth token for API calls (synchronous - returns cached token only)
 * Use this for simple API calls where token should already be cached.
 * For full session refresh, use getAuthTokenAsync().
 */
export function getAuthToken(): string {
  return getCachedToken();
}

/**
 * Get auth token asynchronously with session refresh if needed.
 * Use this when you need to ensure a valid token (e.g., after page load).
 */
export async function getAuthTokenWithRefresh(): Promise<string> {
  const cachedToken = getCachedToken();
  if (cachedToken) {
    return cachedToken;
  }

  try {
    const session = await authGetSession();
    if (session?.token) {
      _updateCachedToken(session.token);
      return session.token;
    }
  } catch (error) {
    console.error("Failed to get token from session:", error);
  }

  return "";
}

/**
 * Alias for getAuthTokenWithRefresh (backward compatibility)
 */
export const getAuthTokenAsync = getAuthTokenWithRefresh;
